import { CurrencyPipe, DatePipe, DecimalPipe, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeCH from '@angular/common/locales/de-CH';
import localeIs from '@angular/common/locales/is';
import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  provideRouter,
  withComponentInputBinding,
  withDisabledInitialNavigation,
  withInMemoryScrolling
} from '@angular/router';

import { CxMaterialConfigProviders } from '@bbraun/cortex';
import { HttpService } from '@bbraun/helpex/src/lib/http';
import {
  provideTransloco,
} from '@jsverse/transloco';
import { provideEffects } from '@ngrx/effects';
import {
  NavigationActionTiming,
  provideRouterStore,
  RouterState
} from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { AllCommunityModule, ModuleRegistry } from 'ag-charts-community';
import { provideOAuthClient } from 'angular-oauth2-oidc';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { DEFAULT_LANGUAGE_KEY, languages } from './app/definitions/constants/language.constants';
import { unauthorizedErrorInterceptor } from './app/interceptors/unauthorized-error.interceptor';
import { userRequestedViewInterceptor } from './app/interceptors/user-requested-view.interceptor';
import { ResiCurrencyPipe } from './app/pipes/resi-currency/resi-currency.pipe';
import { ResiDatePipe } from './app/pipes/resi-date/resi-date.pipe';
import { ResiDecimalPipe } from './app/pipes/resi-decimal/resi-decimal.pipe';
import { AppLanguageService } from './app/services/language/app-language.service';
import { reducers } from './app/stores/global-store';
import { LayoutStoreFacade } from './app/stores/global-store/layout/layout-store.facade';
import { LayoutEffects } from './app/stores/global-store/layout/layout.effects';
import { RouterStoreFacade } from './app/stores/global-store/router/router-store.facade';
import { ROUTER_FEATURE_KEY } from './app/stores/global-store/router/router.selectors';
import { StatusMapEffects } from './app/stores/global-store/status-maps/status-map.effects';
import { StatusMapFacade } from './app/stores/global-store/status-maps/status-map.facade';
import { UserConfigsStoreFacade } from './app/stores/global-store/user-configs/user-configs-store.facade';
import { UserConfigsEffects } from './app/stores/global-store/user-configs/user-configs.effects';
import { SidebarDisplayStore } from './app/stores/signal-stores/sidebar-display-store/sidebar-display.store';
import { TranslocoHttpLoader } from './app/utils/transloco-loader/transloco-loader.service';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

registerLocaleData(localeDe);
registerLocaleData(localeDeCH);
registerLocaleData(localeIs);

// Enable all Community features
ModuleRegistry.registerModules([ AllCommunityModule ]);

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideTransloco({
      config: {
        availableLangs: [ ...languages ],
        defaultLang: DEFAULT_LANGUAGE_KEY,
        reRenderOnLangChange: true,
        fallbackLang: DEFAULT_LANGUAGE_KEY,
        missingHandler: {
          useFallbackTranslation: true
        },
        prodMode: environment.production
      },
      loader: TranslocoHttpLoader
    }),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      withDisabledInitialNavigation()
    ),
    provideOAuthClient({
      resourceServer: { sendAccessToken: true }
    }),
    provideStore(reducers,
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: true,
          strictActionSerializability: true,
          strictActionWithinNgZone: true,
          strictActionTypeUniqueness: true
        }
      }),
    provideEffects([ StatusMapEffects, UserConfigsEffects, LayoutEffects ]),
    provideStoreDevtools(
      {
        maxAge: 25, // Retains last 25 states
        logOnly: environment.production, // Restrict extension to log-only mode
        connectInZone: true
      }
    ),
    provideRouterStore({
      stateKey: ROUTER_FEATURE_KEY,
      routerState: RouterState.Minimal,
      navigationActionTiming: NavigationActionTiming.PreActivation
    }),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        userRequestedViewInterceptor,
        unauthorizedErrorInterceptor
      ]),
    ),
    CxMaterialConfigProviders,
    HttpService,
    LayoutStoreFacade,
    StatusMapFacade,
    RouterStoreFacade,
    UserConfigsStoreFacade,
    AppLanguageService,
    CurrencyPipe,
    ResiCurrencyPipe,
    DatePipe,
    ResiDatePipe,
    DecimalPipe,
    ResiDecimalPipe,
    SidebarDisplayStore,
  ],
}).catch(error => console.error(error));
